import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import PlayArrowIcon     from '@mui/icons-material/PlayArrow';
import PictureAsPdfIcon  from '@mui/icons-material/PictureAsPdf';
import T from '../theme';
import { MODULES, SIDEBAR_SECTIONS } from '../config.jsx';

export function Sidebar({
  track,
  currentModule,
  onSelectModule,
  onRunAll,
  moduleLoading,
  onExportPDF,
  exportingPDF,
  showData,
  runningAll,
  analysisResults,
  moduleErrors,
}) {
  const sections   = SIDEBAR_SECTIONS[track];
  const allModules = MODULES[track];

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        background: T.sidebar,
        borderRight: `1px solid ${T.border}`,
        overflowY: 'auto',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
      }}
    >
      {Object.entries(sections).map(([sectionTitle, moduleIds]) => (
        <Box key={sectionTitle} sx={{ px: 1.5, py: 2, borderBottom: `1px solid ${T.border}` }}>
          <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: T.textSecondary, letterSpacing: 0.5, mb: 1.5, px: 1 }}>
            {sectionTitle}
          </Box>
          {moduleIds.map((id) => {
            const mod      = allModules.find((m) => m.id === id);
            if (!mod) return null;
            const isActive  = currentModule === id;
            const isLoading = moduleLoading[id];
            const isDone    = !!analysisResults?.[id];
            const hasError  = !!moduleErrors?.[id];
            return (
              <Box
                key={id}
                component="button"
                onClick={() => onSelectModule(id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1,
                  py: 1.25,
                  width: '100%',
                  background: isActive ? T.primary : 'none',
                  border: 'none',
                  color: isActive ? '#fff' : T.textSecondary,
                  fontSize: 14,
                  cursor: 'pointer',
                  borderRadius: 0.5,
                  textAlign: 'left',
                  transition: 'all 0.15s',
                  '&:hover': isActive ? {} : { background: T.surface2, color: T.text },
                }}
              >
                {isLoading
                  ? <CircularProgress size={16} sx={{ color: isActive ? '#fff' : T.primary }} />
                  : mod.icon
                }
                <Box component="span" sx={{ flex: 1 }}>{mod.name}</Box>
                {/* Status dot */}
                {isLoading && !isActive && (
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: T.primary, flexShrink: 0, animation: 'pulse13 1.2s infinite', '@keyframes pulse13': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } } }} />
                )}
                {!isLoading && isDone && (
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? 'rgba(255,255,255,0.7)' : T.success, flexShrink: 0 }} />
                )}
                {!isLoading && !isDone && hasError && (
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: T.error, flexShrink: 0 }} />
                )}
              </Box>
            );
          })}
        </Box>
      ))}

      {/* Run All + Export PDF */}
      <Box sx={{ px: 1.5, py: 2, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 1.5, borderTop: `1px solid ${T.border}` }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={runningAll ? <CircularProgress size={14} sx={{ color: T.primary }} /> : <PlayArrowIcon />}
          onClick={onRunAll}
          disabled={runningAll}
          sx={{
            borderColor: runningAll ? T.primary : T.border,
            color: runningAll ? T.primary : T.textSecondary,
            fontSize: 12,
            textTransform: 'none',
            '&:hover': { borderColor: T.primary, color: T.primary, background: 'rgba(249,115,22,0.06)' },
            '&.Mui-disabled': { borderColor: T.primary, color: T.primary, opacity: 0.7 },
          }}
        >
          {runningAll ? 'Running All…' : 'Run All Modules'}
        </Button>

        {showData && (
          <Button
            fullWidth
            variant="contained"
            onClick={onExportPDF}
            disabled={exportingPDF}
            startIcon={exportingPDF ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PictureAsPdfIcon />}
            sx={{
              background: 'linear-gradient(135deg, #f97316 0%, #c2410c 100%)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'none',
              py: 1.25,
              borderRadius: 1.5,
              boxShadow: '0 3px 12px rgba(249,115,22,0.45)',
              letterSpacing: 0.2,
              '&:hover': {
                background: 'linear-gradient(135deg, #fb923c 0%, #ea6317 100%)',
                boxShadow: '0 6px 20px rgba(249,115,22,0.6)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.18s ease',
              '&.Mui-disabled': { background: 'rgba(249,115,22,0.25)', color: 'rgba(255,255,255,0.45)', transform: 'none', boxShadow: 'none' },
            }}
          >
            {exportingPDF ? 'Exporting…' : 'Export PDF Report'}
          </Button>
        )}
      </Box>
    </Box>
  );
}
